/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, forwardRef, Inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator} from '@angular/forms';
import template from './money-field.component.html';
import {formatIsoCurrency} from '../../../../components/util/iso-currency-format';
import {AppLocaleService} from '../../locale/app-locale.service';
import {AdnFormStateService} from '../../../../components/util/adn-form-state.service';
import {LocalNetworkProfile} from '../../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../../components/session/local-user-profile';

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  if (value === '') {
    return true;
  }
  return defaultValue;
}

function normalizeAmount(value: any): number | null {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const cleaned = `${value}`.replace(/,/g, '').replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

@Component({
  standalone: false,
  selector: 'adn-money-field',
  template,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MoneyFieldComponent),
    multi: true
  }, {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MoneyFieldComponent),
    multi: true
  }]
})
export class MoneyFieldComponent implements ControlValueAccessor, Validator, OnChanges, OnInit {
  @Input() name = '';
  @Input() availableCurrencies?: string[];
  @Input() defaultCurrency?: string;
  @Input() fixedWidth = false;
  @Input() updateCurrencyHook?: {update?: (currency: string) => void};

  @Input() set ngModel(value: any) {
    this.hasNgModelInput = true;
    this.boundModelRef = value;
    this.usesFormControl = false;
    this.syncingFromInput = true;
    this.writeValue(value);
    this.syncingFromInput = false;
  }

  @Input() set heavyFormat(value: boolean | string | undefined) {
    this.useHeavyFormat = coerceBooleanAttr(value);
    this.syncAmountDisplay();
  }

  @Input() set isRequired(value: boolean | string | undefined) {
    this.required = coerceBooleanAttr(value);
    this.onValidatorChange();
  }

  @Input() set ngDisabled(value: boolean | string | undefined) {
    this.disabled = coerceBooleanAttr(value);
  }

  @Output() ngModelChange = new EventEmitter<any>();

  currencies: string[] = [];
  money: {amount: number | null; currency: string | null} = {amount: null, currency: null};
  amountDisplay = '';
  amountFocused = false;
  disabled = false;
  required = false;
  useHeavyFormat = false;

  private onChange: (value: any) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  private boundModelRef: any = null;
  private usesFormControl = false;
  private syncingFromInput = false;
  private hasNgModelInput = false;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly appLocale: AppLocaleService,
    @Inject(AdnFormStateService) private readonly adnFormState?: any
  ) {
  }

  get amountName(): string {
    return this.name ? `${this.name}.amount` : 'amount';
  }

  get currencyName(): string {
    return this.name ? `${this.name}.currency` : 'currency';
  }

  ngOnInit(): void {
    this.registerUpdateCurrencyHook();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.availableCurrencies || changes.defaultCurrency) {
      this.refreshCurrencies();
      this.ensureCurrency();
      this.syncAmountDisplay();
      this.onValidatorChange();
    }

    if (changes.updateCurrencyHook) {
      this.registerUpdateCurrencyHook();
    }
  }

  writeValue(value: any): void {
    if (!this.syncingFromInput) {
      this.usesFormControl = !this.hasNgModelInput;
    }

    this.money = {
      amount: normalizeAmount(value?.amount),
      currency: value?.currency || null
    };
    this.refreshCurrencies();
    this.ensureCurrency();
    this.syncAmountDisplay();
    this.onValidatorChange();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.onValidatorChange();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    void control;
    if (this.disabled || !this.required) {
      return this.validateAmountBounds();
    }

    if (!Number.isFinite(this.money.amount) || !this.money.currency) {
      return {required: true};
    }

    return this.validateAmountBounds();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
    fn();
  }

  private validateAmountBounds(): ValidationErrors | null {
    if (Number.isFinite(this.money.amount) && this.money.amount < 0) {
      return {min: {min: 0, actual: this.money.amount}};
    }

    return null;
  }

  trackByCurrency(_: number, currency: string): string {
    return currency;
  }

  onAmountInput(value: any): void {
    if (this.useHeavyFormat) {
      this.amountDisplay = `${value ?? ''}`;
      this.money.amount = normalizeAmount(value);
      this.ensureCurrency();
      this.emitValue();
      return;
    }

    this.onAmountChange(value);
  }

  onAmountFocus(): void {
    this.amountFocused = true;
    if (this.useHeavyFormat && Number.isFinite(this.money.amount)) {
      this.amountDisplay = `${this.money.amount}`;
    }
  }

  onAmountBlur(): void {
    this.amountFocused = false;
    if (this.useHeavyFormat) {
      this.onAmountChange(this.amountDisplay);
    }
    this.syncAmountDisplay();
    this.onTouched();
  }

  onHeavyFormatKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.altKey || event.metaKey || typeof event.key !== 'string' || event.key.length !== 1) {
      return;
    }

    if (!Number.isFinite(parseInt(event.key, 10)) && event.key !== '.') {
      event.preventDefault();
    }
  }

  onAmountChange(value: any): void {
    this.money.amount = normalizeAmount(value);
    this.ensureCurrency();
    this.emitValue(true);
  }

  onCurrencyChange(currency: string): void {
    this.money.currency = currency || null;
    this.emitValue(true);
  }

  private emitValue(userInitiated = true): void {
    const value = {
      amount: this.money.amount,
      currency: this.money.currency
    };

    // When bound via [(ngModel)] to an ngFor local (e.g. fee currency rows),
    // emit the same object reference after mutating it. Emitting a new object
    // only reassigns the template local, so the next change-detection pass
    // writes the stale array value back and truncates multi-digit input.
    let emitted = value;
    if (!this.usesFormControl && this.boundModelRef && typeof this.boundModelRef === 'object') {
      this.boundModelRef.amount = value.amount;
      this.boundModelRef.currency = value.currency;
      emitted = this.boundModelRef;
    }

    this.onTouched();
    this.onValidatorChange();
    this.onChange(emitted);
    this.ngModelChange.emit(emitted);
    this.syncAmountDisplay();

    if (userInitiated && !this.usesFormControl && typeof this.adnFormState?.markDirty === 'function') {
      this.adnFormState.markDirty();
    }
  }

  private syncAmountDisplay(): void {
    if (!this.useHeavyFormat || this.amountFocused) {
      return;
    }

    if (!Number.isFinite(this.money.amount)) {
      this.amountDisplay = '';
      return;
    }

    const formatted = formatIsoCurrency(
      this.money.amount,
      this.money.currency,
      null,
      true,
      true,
      this.LocalNetworkProfile.get('defaultCurrency'),
      this.appLocale.getLocaleId()
    );
    this.amountDisplay = formatted ? `${formatted}` : `${this.money.amount}`;
  }

  private registerUpdateCurrencyHook(): void {
    if (!this.updateCurrencyHook || typeof this.updateCurrencyHook !== 'object') {
      return;
    }

    this.updateCurrencyHook.update = (theCurrency: string) => {
      this.applyExternalCurrency(theCurrency);
    };
  }

  private applyExternalCurrency(theCurrency: string): void {
    const currency = this.money.currency;
    const amount = this.money.amount;
    if (!currency || !Number.isFinite(amount)) {
      const fallbackCurrency = theCurrency || this.defaultCurrency || this.LocalNetworkProfile.get('defaultCurrency');
      const preferredCurrency = this.localUserProfile.getPreferredCurrency?.();
      const selectedCurrency = preferredCurrency && this.currencies.indexOf(preferredCurrency) > -1
        ? preferredCurrency
        : (this.currencies.indexOf(fallbackCurrency) > -1 ? fallbackCurrency : this.currencies[0]);
      this.money.currency = selectedCurrency || null;
      this.emitValue(false);
      return;
    }

    if (currency && this.currencies.indexOf(currency) < 0) {
      this.currencies = this.currencies.concat(currency);
    }
  }

  private refreshCurrencies(): void {
    const currencies = Array.isArray(this.availableCurrencies) && this.availableCurrencies.length > 0
      ? this.availableCurrencies.slice()
      : (this.LocalNetworkProfile.get('availableCurrencies') || []).slice();

    if (this.money.currency && currencies.indexOf(this.money.currency) < 0) {
      currencies.push(this.money.currency);
    }

    const fallbackCurrency = this.resolveDefaultCurrency();
    if (!currencies.length && fallbackCurrency) {
      currencies.push(fallbackCurrency);
    }

    this.currencies = currencies;
  }

  private ensureCurrency(): void {
    if (this.money.currency && this.currencies.indexOf(this.money.currency) > -1) {
      return;
    }

    const fallbackCurrency = this.resolveDefaultCurrency();
    this.money.currency = fallbackCurrency || this.currencies[0] || null;

    if (this.money.currency && this.currencies.indexOf(this.money.currency) < 0) {
      this.currencies = this.currencies.concat(this.money.currency);
    }
  }

  private resolveDefaultCurrency(): string | null {
    const preferredCurrency = this.localUserProfile.getPreferredCurrency?.();
    const configuredDefault = this.defaultCurrency || this.LocalNetworkProfile.get('defaultCurrency') || null;

    if (preferredCurrency && this.currencies.indexOf(preferredCurrency) > -1) {
      return preferredCurrency;
    }

    return configuredDefault;
  }
}
